import React, { useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, TriangleAlert } from 'lucide-react';
import { ToastContext, type Toast, type ToastType } from './ToastContext';

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((state) => state.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType = 'success') => {
    // Math.random é mais seguro que crypto.randomUUID em alguns navegadores/http
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { id, message, type };

    console.log('🔔 Toast Disparado:', message); // Log para debug

    setToasts((state) => [...state, newToast]);

    setTimeout(() => {
      removeToast(id);
    }, 4000);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      
      {/* Z-Index 99999 para garantir que fique acima de TUDO.
          Removi as classes "animate-in" que podem estar falhando.
      */}
      <div 
        className="fixed top-4 right-4 flex flex-col gap-2 w-full max-w-sm pointer-events-none"
        style={{ zIndex: 99999 }} 
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              pointer-events-auto flex items-start gap-3 p-4 rounded-xl shadow-2xl border
              transition-all duration-300 transform translate-x-0 opacity-100
              ${toast.type === 'success' ? 'bg-white dark:bg-slate-900 border-green-500 text-green-800 dark:text-green-200' : ''}
              ${toast.type === 'error' ? 'bg-white dark:bg-slate-900 border-red-500 text-red-800 dark:text-red-200' : ''}
              ${toast.type === 'info' ? 'bg-white dark:bg-slate-900 border-blue-500 text-blue-800 dark:text-blue-200' : ''}
              ${toast.type === 'warning' ? 'bg-white dark:bg-slate-900 border-amber-500 text-amber-800 dark:text-amber-200' : ''}
            `}
            style={{ 
                animation: 'slideInRight 0.3s ease-out forwards',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="shrink-0 mt-0.5">
              {toast.type === 'success' && <CheckCircle size={20} className="text-green-600 dark:text-green-400" />}
              {toast.type === 'error' && <AlertCircle size={20} className="text-red-600" />}
              {toast.type === 'info' && <Info size={20} className="text-blue-600 dark:text-blue-400" />}
              {toast.type === 'warning' && <TriangleAlert size={20} className="text-amber-600 dark:text-amber-400" />}
            </div>

            <p className="flex-1 text-sm font-bold leading-snug">
              {toast.message}
            </p>

            <button
              onClick={() => removeToast(toast.id)}
              className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:text-slate-500 transition-colors p-1"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Estilo Injetado para garantir a animação sem plugin */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
};
